import React from 'react';
import { Car, Sparkles } from 'lucide-react';
import { useInView } from './ui';
import { PILLARS } from './content';

const PillarCard = ({ p, i }) => (
  <div className="relative z-10 rounded-2xl border border-[#e2e0d8] bg-[#ffffff] p-5 shadow-sm" data-testid={`fit-pillar-${p.key}`}>
    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#0f766e]">0{i + 1}</p>
    <h3 className="dl-display mt-1 text-xl font-semibold text-[#0f172a]">{p.title}</h3>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {p.items.map((it) => <span key={it} className="rounded-full bg-[#f1efe9] px-2.5 py-1 text-xs font-medium text-[#334155]">{it}</span>)}
    </div>
  </div>
);

export const FitDiagram = () => {
  const [ref, seen] = useInView(0.25);
  return (
    <div ref={ref} className="relative" data-testid="fit-diagram">
      <svg className={`dl-draw pointer-events-none absolute inset-0 hidden h-full w-full md:block ${seen ? 'is-in' : ''}`} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {[[18, 22], [82, 22], [18, 78], [82, 78]].map(([x, y]) => (
          <line key={`${x}${y}`} x1="50" y1="50" x2={x} y2={y} stroke="#0f766e" strokeWidth="0.35" strokeDasharray="1.2 1.2" vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="grid gap-4 md:grid-cols-3 md:grid-rows-2 md:gap-x-10 md:gap-y-24">
        <PillarCard p={PILLARS[0]} i={0} />
        <div className="order-first flex items-center justify-center md:order-none md:row-span-2">
          <div className={`relative flex h-48 w-48 flex-col items-center justify-center rounded-full bg-[#0f172a] text-center shadow-2xl transition-transform duration-700 ${seen ? 'scale-100' : 'scale-90'}`}>
            <span className="absolute inset-[-10px] animate-[spin_24s_linear_infinite] rounded-full border border-dashed border-[#5eead4]/60" />
            <Car className="text-[#5eead4]" size={34} />
            <p className="dl-display mt-2 text-lg font-semibold text-[#f8fafc]">The car</p>
            <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#5eead4]"><Sparkles size={12} /> Drive IQ at the centre</p>
          </div>
        </div>
        <PillarCard p={PILLARS[1]} i={1} />
        <PillarCard p={PILLARS[2]} i={2} />
        <PillarCard p={PILLARS[3]} i={3} />
      </div>
    </div>
  );
};

export const FlowDiagram = ({ steps, dark, testid = 'flow-diagram' }) => {
  const [ref, seen] = useInView(0.3);
  return (
    <ol ref={ref} className="relative grid gap-4 sm:grid-flow-col sm:auto-cols-fr sm:gap-3" data-testid={testid}>
      <span className={`absolute left-[19px] top-5 h-[calc(100%-40px)] w-0.5 origin-top sm:left-5 sm:right-5 sm:top-[19px] sm:h-0.5 sm:w-auto sm:origin-left ${dark ? 'bg-[#5eead4]/50' : 'bg-[#0f766e]/40'} transition-transform duration-[1400ms] ${seen ? 'scale-100' : 'scale-0'}`} aria-hidden="true" />
      {steps.map((s, i) => (
        <li key={s} className={`dl-reveal relative flex items-start gap-3 sm:flex-col ${seen ? 'is-in' : ''}`} style={{ transitionDelay: `${i * 140}ms` }}>
          <span className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${dark ? 'bg-[#5eead4] text-[#042f2e]' : 'bg-[#0f172a] text-[#f8fafc]'}`}>{i + 1}</span>
          <span className={`pt-2 text-sm font-semibold leading-snug sm:pt-0 ${dark ? 'text-[#e2e8f0]' : 'text-[#0f172a]'}`}>{s}</span>
        </li>
      ))}
    </ol>
  );
};
